/**
 * Module-level state for the playlist asset picker's last browsed folder path.
 * Extracted to its own file so logout.ts can reset it without importing
 * the heavy AssetPickerPanel (which pulls in Uppy, dnd-kit, etc.).
 *
 * sessionStorage is the source of truth for the current folder context.
 * The Assets page writes to it via usePersistedPath('assets'), and
 * AssetPickerPanel / SelectAssetModal read and write via setPersistedPath/clearPersistedPath.
 * Module-level lastPickerContext is used only for the notify/subscribe mechanism —
 * getPickerPathContext() always trusts sessionStorage (empty = root).
 *
 * Returns full context { path, teamId?, shared? } so shared folders from other
 * teams query with the correct teamId (not the user's activeTeamId).
 */

import { PERSISTED_PATH_PREFIX, PERSISTED_PATH_CHANGE_EVENT } from '@/hooks/usePersistedPath';

export interface PickerPathContext {
  path: string;
  teamId?: string;
  /** When true, picker should enter "Shared with me" mode */
  shared?: boolean;
}

/** Assets page uses this special path value for "Shared with me" root */
const SHARE_WITH_ME_PATH = 'SHARE_WITH_ME';

const ROOT_CONTEXT: PickerPathContext = { path: '/' };

let lastPickerContext: PickerPathContext = { path: '/' };

// --- Subscription support (useSyncExternalStore) ---
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Subscribe to picker path changes. Returns unsubscribe function.
 *  Listens to both direct setPickerPath() calls AND sessionStorage writes
 *  from other pages (e.g., Assets page navigating folders). */
export function subscribePickerPath(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener(PERSISTED_PATH_CHANGE_EVENT, listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener(PERSISTED_PATH_CHANGE_EVENT, listener);
  };
}

/** Stable-reference snapshot for useSyncExternalStore.
 *  Always reads fresh from sessionStorage, but returns the
 *  same object reference when nothing changed (deep compare) so
 *  useSyncExternalStore doesn't trigger unnecessary re-renders. */
let cachedSnapshot: PickerPathContext = { path: '/' };

export function getPickerPathSnapshot(): PickerPathContext {
  const fresh = getPickerPathContext();
  if (
    cachedSnapshot.path === fresh.path &&
    cachedSnapshot.teamId === fresh.teamId &&
    cachedSnapshot.shared === fresh.shared
  ) {
    return cachedSnapshot;
  }
  cachedSnapshot = fresh;
  return cachedSnapshot;
}

export function getPickerPathContext(): PickerPathContext {
  // sessionStorage is the source of truth — written by usePersistedPath('assets'),
  // setPersistedPath('assets'), and clearPersistedPath('assets').
  // Empty sessionStorage means root (Assets page at root or never visited).
  try {
    const stored = sessionStorage.getItem(`${PERSISTED_PATH_PREFIX}assets`);
    if (stored) {
      const parsed: Record<string, string> = JSON.parse(stored);
      if (!parsed.path || parsed.path === '/') return ROOT_CONTEXT;

      // Assets page at "Shared with me" root → picker should enter shared mode at root
      if (parsed.path === SHARE_WITH_ME_PATH) {
        return { path: '/', shared: true };
      }

      // Inside a shared folder → preserve full context
      if (parsed.shared) {
        return {
          path: parsed.path,
          teamId: parsed.teamId,
          shared: true,
        };
      }

      // Normal folder
      return { path: parsed.path, teamId: parsed.teamId };
    }
  } catch {
    // Invalid stored data — ignore
  }
  return ROOT_CONTEXT;
}

/** @deprecated Use getPickerPathContext() for full shared-folder support */
export function getPickerPath() {
  return getPickerPathContext().path;
}

export function setPickerPath(path: string) {
  if (lastPickerContext.path === path) return;
  lastPickerContext = { ...lastPickerContext, path };
  notify();
}

/** Resets the picker path to root. Called on logout to prevent cross-account path leakage. */
export function resetPickerPath() {
  lastPickerContext = { path: '/' };
  notify();
}
